/* eslint-disable jsx-a11y/alt-text */
import { forwardRef, useEffect, useRef, useState } from "react";
import ICONS from "../../../assets/Icons";

/**
 * @typedef {("verified" | "error" | "typing" | "default")} TypeVariants
 * @typedef {("big" | "small")} FontVariants
 */
/**
 * @typedef PropType
 * @type {object}
 * @property {string} placeholder - Text to show in the placeholder
 * @property {TypeVariants} variant - one of "error", "verified", "typing", or "default"
 * @property {string} bgColor - background colour of element that this input field is on top of (var only)
 * @property {string} fgColor - The foreground colour of this input field. (var only)
 * @property {string} placeholderColor - color of placeholder text (var only)
 * @property {string} adornment - The icon to show in the input field.
 * - If this is not given, no icon will be shown in typing and default states and the
 * default error and verified icons will be shown in their respective variants
 * - If this is the empty string ("") then no icon will be shown whatsoever.
 * @property {React.FormEventHandler<HtmlElement>} [onAdornmentClick] - This function is called when the adornment is clicked.
 * @property {React.FormEventHandler<HTMLInputElement>} [onInput] - This function is given to "onInput" of input tag
 * @property {string} [value] - The value attribute of the input tag
 * @property {string} [name] - The value of the name attribute of the underlying <input>
 * @property {FontVariants} [fontKind="big"] - Use "big" font or "small" font. defaults to big.
 * @property {Boolean} isDisabled - Whether this input field is disabled.
 * @property {Boolean} isFullWidth - Whether this input field is full width.
 * @property {React.FocusEventHandler<HTMLInputElement>} [onFocus]
 * @property {React.FocusEventHandler<HTMLInputElement>} [onBlur]
 */
/**
 * @type {React.ForwardRefRenderFunction<HtmlHTMLInputElement, PropType>}
 */
export default forwardRef(function (
  {
    placeholder,
    variant,
    bgColor,
    fgColor,
    placeholderColor = "var(--text-secondary)",
    adornment,
    onInput,
    onFocus,
    onBlur,
    value,

    fontKind,
    name,
    isDisabled,
    isFullWidth,
    onAdornmentClick,
  },
  inputBoxRef,
) {
  if (!adornment?.length && onAdornmentClick) {
    throw new Error(
      "Impossible to have an adornment click handler without an adornment",
    );
  }

  const [variantInfo, setVariantInfo] = useState({
    color: "var(--text-primary)",
    adornment: null,
    fontKind: fontKind === "small" ? "var(--font-xs)" : "var(--font-md)",
    adornmentDimension:
      fontKind && fontKind === "small" ? "var(--icon-sm)" : "var(--icon-md)",
    placeholderColor: "var(--text-primary)",
  });
  const [oldVariant, setOldVariant] = useState({});

  useEffect(() => {
    let vi;
    switch (variant) {
      case "verified":
        vi = {
          ...variantInfo,
          color: "var(--positive-act)",
          adornment: ICONS.VERIFIED_WITH_WHITE_CIRCLE_GREEN,
          placeholderColor: "var(--positive-act)",
        };
        break;
      case "error":
        vi = {
          ...variantInfo,
          color: "var(--negative-act)",
          adornment: ICONS.ERROR_WITH_WHITE_OUTLINED_CIRCLE,
          placeholderColor: "var(--negative-act)",
        };
        break;
      case "typing":
        vi = {
          ...variantInfo,
          color: "var(--pending)",
          adornment: null,
          placeholderColor: "var(--pending)",
        };
        break;
      default:
        vi = {
          ...variantInfo,
          color: fgColor ?? "var(--text-primary)",
          adornment: null,
          placeholderColor: placeholderColor ?? fgColor,
        };
        break;
    }
    setVariantInfo(vi);
    setOldVariant(vi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  useEffect(() => {
    setVariantInfo((i) => {
      let vi = {
        ...i,
        fontKind:
          fontKind && fontKind === "small"
            ? "var(--font-xs)"
            : "var(--font-md)",
        adornmentDimension:
          fontKind && fontKind === "small"
            ? "var(--icon-sm)"
            : "var(--icon-md)",
      };
      setOldVariant(vi);
      return vi;
    });
  }, [fontKind]);

  useEffect(() => {
    if (value) {
      setIsFocused(true);
    }
  }, [value]);

  const iRef = useRef(null);
  inputBoxRef ??= iRef;

  const [isFocused, setIsFocused] = useState(false);
  const placeHolderRef = useRef(null);
  return (
    <div
      onClick={(e) => {
        if (isDisabled) return;
        e.preventDefault();
        e.stopPropagation();
        if (inputBoxRef?.current?.value) {
          setIsFocused(true);
          return;
        }
        setIsFocused(true);
        inputBoxRef?.current?.focus();
      }}
      className={`border  flex flex-row items-center py-padding-sm px-padding-md justify-between gap-gap-md rounded-full w-full relative ${
        isFullWidth ? "" : "max-w-input-max-width min-w-input-min-width"
      }`}
      role="textbox"
      style={{
        fontSize: variantInfo.fontKind,
        borderColor: variantInfo.color,
        opacity: isDisabled ? "var(--light-opacity)" : "var(--base-opacity)",
        cursor: isDisabled ? "not-allowed" : "default",
      }}
    >
      <div
        className="transition-all px-padding-xs"
        ref={placeHolderRef}
        style={{
          userSelect: "none",
          backgroundColor: bgColor,
          color: isFocused ? variantInfo.color : variantInfo.placeholderColor,
          transform: isFocused
            ? "translateY(calc(-2*var(--padding-sm)))"
            : "translateY(0)",
          fontSize: isFocused ? "var(--font-xs)" : variantInfo.fontKind,
          position: "absolute",
        }}
      >
        {placeholder}
      </div>
      <input
        disabled={isDisabled}
        value={value}
        onInput={onInput}
        onFocus={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsFocused(true);
          // if (!["default", "error"].includes(variant)) {
          if (variant !== "default") {
            setVariantInfo({
              ...variantInfo,
            });
          } else {
            setVariantInfo({
              ...variantInfo,
              color: "var(--pending)",
            });
          }
          if (typeof onFocus === "function") onFocus(e);
        }}
        onBlur={(e) => {
          setVariantInfo(oldVariant);
          if (inputBoxRef?.current?.value) {
            if (typeof onBlur === "function") onBlur(e);
            return;
          }
          setIsFocused(false);
          if (typeof onBlur === "function") onBlur(e);
        }}
        ref={inputBoxRef}
        name={name ?? "inputBox"}
        style={{
          fontSize: variantInfo.fontKind,
          color: fgColor ?? variantInfo.color,
          backgroundColor: bgColor,
          cursor: isDisabled ? "not-allowed" : "default",
          userSelect: isDisabled ? "none" : "auto",
        }}
        type="text"
        className="w-full"
      />
      {adornment !== "" && (
        <div
          onClick={onAdornmentClick ?? ((e) => {})}
          style={{
            width: variantInfo.adornmentDimension,
            height: variantInfo.adornmentDimension,
            cursor: onAdornmentClick ? "pointer" : "default",
          }}
        >
          <img
            style={{
              maxWidth: variantInfo.adornmentDimension,
              height: variantInfo.adornmentDimension,
            }}
            src={adornment ?? variantInfo.adornment}
          />
        </div>
      )}
    </div>
  );
});
